"use client"

import { useEffect, useState, useRef } from "react"
import API from "../services/api"
import {
  menusArrayFromResponse,
  filterItemsForVenueClient,
  getMenuItemDisplayPrice,
  getVenueDisplayPrice,
} from "../lib/venue-menus-client/helpers"
import {
  ITEM_ALLERGEN_FIELDS,
  ALLERGEN_CLIENT_TRANSLATIONS,
} from "../lib/venue-menus-client/menuItemConstants"
import AllergenIcon from "../components/AllergenIcon"
import "./RestaurantsMenusNew.css"
import "./client-image-fix-dark.css"
import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight, LayoutGrid, Book, CheckCircle2, XCircle } from "lucide-react"

// Translation system
const translations = {
  fr: {
    // Header
    back: "Retour",
dietaryInformation: "Informations alimentaires :",
vegetarian: "Végétarien",
organic: "Bio",
local: "Local",
allergenInformation: "Allergènes :",
    // Language Selector
    switchToModern: "Passer à la vue moderne",
    switchToBook: "Passer à la vue livre",

    // Welcome Banner
    restaurantsAndMenus: "Restaurants & Menus",
    discoverRestaurants: "Découvrez nos restaurants et leurs spécialités culinaires",

    // Loading & Empty States
    loadingRestaurants: "Chargement des restaurants...",
    loadingMenus: "Chargement des menus...",
    noMenuAvailable: "Aucun menu disponible",
    noDishAvailable: "Aucun plat disponible dans ce menu",
    noRestaurantsFound: "Aucun restaurant trouvé",
    comeBackSoonRestaurants: "Revenez bientôt pour découvrir nos restaurants",
    comeBackSoonMenus: "Revenez bientôt pour découvrir nos menus",

    // Restaurant Card Buttons
    viewMenu: "Voir le menu",
    reserveTable: "Réserver une table",

    // Menu Navigation
    previousMenu: "Menu précédent",
    nextMenu: "Menu suivant",
    previousImage: "Image précédente",
    nextImage: "Image suivante",
    goToImage: "Aller à l'image", // For image dots aria-label

    // Modal
    reserveTableModal: "Réserver une table",
    name: "Nom",
    email: "Email",
    phoneNumber: "Numéro de téléphone",
    numberOfPeople: "Nombre de personnes",
    reserve: "Réserver",
    cancel: "Annuler",
    reservationSuccess: "Réservation créée avec succès !",
    reservationSuccessTitle: "Réservation confirmée !",
    reservationSuccessMessage: "Votre demande a bien été enregistrée. Notre équipe vous contactera rapidement pour confirmer votre table.",
    reservationErrorTitle: "Réservation impossible",
    reservationError: "Erreur lors de la création de la réservation.",
    ok: "Parfait",
    submitting: "Envoi en cours...",

    // Footer (re-use from previous components if consistent)
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Réseau Accor",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",

    // Vegetarian Filter
    showingVegetarian: "Végétarien uniquement",
    showAll: "Tous les plats",
    noVegetarianDishes: "Aucun plat végétarien disponible",
  },
  en: {
    // Header
    back: "Back",

    // Language Selector
    switchToModern: "Switch to modern view",
    switchToBook: "Switch to book view",
dietaryInformation: "Dietary Information:",
vegetarian: "Vegetarian",
organic: "Organic",
local: "Local",
allergenInformation: "Allergens:",
    // Welcome Banner
    restaurantsAndMenus: "Restaurants & Menus",
    discoverRestaurants: "Discover our restaurants and their culinary specialties",

    // Loading & Empty States
    loadingRestaurants: "Loading restaurants...",
    loadingMenus: "Loading menus...",
    noMenuAvailable: "No menu available",
    noDishAvailable: "No dish available in this menu",
    noRestaurantsFound: "No restaurants found",
    comeBackSoonRestaurants: "Come back soon to discover our restaurants",
    comeBackSoonMenus: "Come back soon to discover our menus",

    // Restaurant Card Buttons
    viewMenu: "View Menu",
    reserveTable: "Reserve a Table",

    // Menu Navigation
    previousMenu: "Previous Menu",
    nextMenu: "Next Menu",
    previousImage: "Previous image",
    nextImage: "Next image",
    goToImage: "Go to image",

    // Modal
    reserveTableModal: "Reserve a Table",
    name: "Name",
    email: "Email",
    phoneNumber: "Phone Number",
    numberOfPeople: "Number of People",
    reserve: "Reserve",
    cancel: "Cancel",
    reservationSuccess: "Reservation created successfully!",
    reservationSuccessTitle: "Reservation confirmed!",
    reservationSuccessMessage: "Your request has been received. Our team will contact you shortly to confirm your table.",
    reservationErrorTitle: "Reservation failed",
    reservationError: "Error creating reservation.",
    ok: "OK",
    submitting: "Submitting...",

    // Footer
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "accor network",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",

    // Vegetarian Filter
    showingVegetarian: "Vegetarian only",
    showAll: "All dishes",
    noVegetarianDishes: "No vegetarian dishes available",
  },
  ar: {
    // Header
    back: "رجوع",

    // Language Selector
    switchToModern: "التبديل إلى العرض الحديث",
    switchToBook: "التبديل إلى عرض الكتاب",

    // Welcome Banner
    restaurantsAndMenus: "المطاعم والقوائم",
    discoverRestaurants: "اكتشف مطاعمنا وتخصصاتنا الطهوية",

    // Loading & Empty States
    loadingRestaurants: "جاري تحميل المطاعم...",
    loadingMenus: "جاري تحميل القوائم...",
    noMenuAvailable: "لا توجد قائمة متاحة",
    noDishAvailable: "لا يوجد طبق متاح في هذه القائمة",
    noRestaurantsFound: "لم يتم العثور على مطاعم",
    comeBackSoonRestaurants: "عد قريبًا لاكتشاف مطاعمنا",
    comeBackSoonMenus: "عد قريبًا لاكتشاف قوائمنا",
dietaryInformation: "معلومات غذائية:",
vegetarian: "نباتي",
organic: "عضوي",
local: "محلي",
allergenInformation: "مسببات الحساسية:",
    // Restaurant Card Buttons
    viewMenu: "عرض القائمة",
    reserveTable: "حجز طاولة",

    // Menu Navigation
    previousMenu: "القائمة السابقة",
    nextMenu: "القائمة التالية",
    previousImage: "الصورة السابقة",
    nextImage: "الصورة التالية",
    goToImage: "الانتقال إلى الصورة",

    // Modal
    reserveTableModal: "حجز طاولة",
    name: "الاسم",
    email: "البريد الإلكتروني",
    phoneNumber: "رقم الهاتف",
    numberOfPeople: "عدد الأشخاص",
    reserve: "حجز",
    cancel: "إلغاء",
    reservationSuccess: "تم إنشاء الحجز بنجاح!",
    reservationSuccessTitle: "تم تأكيد الحجز!",
    reservationSuccessMessage: "تم تسجيل طلبكم بنجاح. سيتواصل معكم فريقنا قريباً لتأكيد طاولتكم.",
    reservationErrorTitle: "تعذّر إتمام الحجز",
    reservationError: "حدث خطأ أثناء إنشاء الحجز.",
    ok: "حسناً",
    submitting: "جاري الإرسال...",

    // Footer
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",

    // Vegetarian Filter
    showingVegetarian: "نباتي فقط",
    showAll: "جميع الأطباق",
    noVegetarianDishes: "لا يوجد أطباق نباتية متاحة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]



const renderDietaryBadges = (item) => (
  <>
    {item.isVegetarian && <span className="dietary-badge">🌱</span>}
    {item.isOrganic && <span className="dietary-badge">🌿</span>}
    {item.isLocal && <span className="dietary-badge">🏠</span>}
  </>
)

const renderAllergenBadges = (item, lang) => {
  const labels = ALLERGEN_CLIENT_TRANSLATIONS[lang] || ALLERGEN_CLIENT_TRANSLATIONS.fr
  return ITEM_ALLERGEN_FIELDS.filter((field) => item[field]).map((field) => (
    <span key={field} className="allergen-badge">
      <AllergenIcon field={field} size={14} className="allergen-badge-icon" />
      {labels[field]}
    </span>
  ))
}

const VenueMenusClient = ({ config: venueConfig }) => {
  const {
    venuesEndpoint,
    pageContentPageName,
    reservationService,
    filterMenu,
    labelOverrides = { fr: {}, en: {}, ar: {} },
  } = venueConfig

  const [restaurants, setRestaurants] = useState([])
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)
  const [menus, setMenus] = useState([])
  const [currentMenuIndex, setCurrentMenuIndex] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0) // For image carousel
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const bookRef = useRef(null)
  const [showModal, setShowModal] = useState(false)
  const [reservationFeedback, setReservationFeedback] = useState(null)
  const [isSubmittingReservation, setIsSubmittingReservation] = useState(false)
  const [reservationData, setReservationData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    from: "",
    to: new Date().toISOString().split("T")[0],
    people: null,
  })
  const [isMobile, setIsMobile] = useState(false)
  const [viewMode, setViewMode] = useState("book") // "book" or "modern"
  const [headerImageIndex, setHeaderImageIndex] = useState(0)
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Langue partagée avec l'écran d'accueil (Home) via IBIS-language
    if (typeof window === "undefined") return "fr"
    const saved = (localStorage.getItem("IBIS-language") || "").trim().toLowerCase()
    if (saved && translations[saved]) return saved
    return "fr"
  })
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [showVegetarianOnly, setShowVegetarianOnly] = useState(false)
  const [pageContent, setPageContent] = useState(null)

  const t = (key) =>
    labelOverrides[currentLanguage]?.[key] ??
    translations[currentLanguage][key] ??
    translations.fr[key] ??
    key

  // Helpers: afficher le contenu selon la langue sélectionnée
  const getRestaurantName = (restaurant) => {
    const tr = restaurant?.translations || {}
    return tr[currentLanguage]?.name || tr.fr?.name || restaurant?.name || ""
  }
  const getMenuTitle = (menu) => {
    const tr = menu?.translations || {}
    return tr[currentLanguage]?.title || tr.fr?.title || menu?.title || ""
  }
  const getItemName = (item) => {
    const tr = item?.translations || {}
    return tr[currentLanguage]?.name || tr.fr?.name || item?.name || ""
  }
  const getItemDescription = (item) => {
    const tr = item?.translations || {}
    return tr[currentLanguage]?.description ?? tr.fr?.description ?? item?.description ?? ""
  }
  /** Prix plat si renseigné (price / prix / traductions) — Encas, petit déj, restaurant */
  const getItemPriceAmount = (item) => getMenuItemDisplayPrice(item, currentLanguage)
  const getVenuePriceAmount = (venue) => getVenueDisplayPrice(venue, currentLanguage)
  const getPageContentDescription = () => {
    if (!pageContent) return ""
    const tr = pageContent.translations || {}
    return tr[currentLanguage]?.description ?? tr.fr?.description ?? pageContent.description ?? ""
  }
  const getPageContentImage = () => {
    if (!pageContent) return null
    const tr = pageContent.translations || {}
    return tr[currentLanguage]?.image ?? tr.fr?.image ?? pageContent.image ?? null
  }

  const getPageContentHeroImageUrl = () => {
    if (!pageContent) return null
    return getPageContentImage() || pageContent.images?.[0] || null
  }

    const DietaryLegend = () => {
    const allergenLabels = ALLERGEN_CLIENT_TRANSLATIONS[currentLanguage] || ALLERGEN_CLIENT_TRANSLATIONS.fr
    return (
      <div className="dietary-legend">
        <h4>{t("dietaryInformation")}</h4>
        <div className="legend-items">
          <span className="legend-item">🌱 {t("vegetarian")}</span>
          <span className="legend-item">🌿 {t("organic")}</span>
          <span className="legend-item">🏠 {t("local")}</span>
        </div>
        <h4 style={{ marginTop: "0.75rem" }}>{t("allergenInformation")}</h4>
        <div className="legend-items legend-items-allergens">
          {ITEM_ALLERGEN_FIELDS.map((field) => (
            <span key={field} className="legend-item legend-item-allergen">
              <AllergenIcon field={field} size={14} className="allergen-legend-icon" />
              {allergenLabels[field]}
            </span>
          ))}
        </div>
      </div>
    )
  }

  const fetchRestaurants = async () => {
    try {
      setIsLoading(true)
      const res = await API.get(venuesEndpoint)
      const raw = res.data
      setRestaurants(Array.isArray(raw) ? raw : [])
      setIsLoaded(true)
    } catch {
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMenus = async (restaurantId) => {
    try {
      setIsLoading(true)
      const res = await API.get("/menus")
      const list = menusArrayFromResponse(res.data)
      const filteredMenus = list.filter((menu) => filterMenu(menu, restaurantId))
      setMenus(filteredMenus)
      setCurrentMenuIndex(0)
      setCurrentImageIndex(0)
    } catch {
    } finally {
      setIsLoading(false)
    }
  }

  const handleReservationSubmit = async () => {
    if (isSubmittingReservation) return
    setIsSubmittingReservation(true)
    try {
      const payload = {
        ...reservationData,
        service: reservationService,
        serviceDetails: getRestaurantName(selectedRestaurant) || "",
        status: "pending",
      }
      await API.post("/reservations", payload)
      setShowModal(false)
      setReservationData({
        name: "",
        email: "",
        phoneNumber: "",
        from: "",
        to: new Date().toISOString().split("T")[0],
        people: null,
      })
      setReservationFeedback("success")
    } catch {
      setShowModal(false)
      setReservationFeedback("error")
    } finally {
      setIsSubmittingReservation(false)
    }
  }

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const res = await API.get(`/page-contents/page/${pageContentPageName}`)
        setPageContent(res.data)
      } catch {
        setPageContent(null)
      }
    }
    fetchPageContent()
  }, [pageContentPageName])


  useEffect(() => {
    fetchRestaurants()
    window.scrollTo(0, 0) // Scroll to the top of the page on mount
  }, [])

  useEffect(() => {
    // Resync with language selected on Home (same key IBIS-language)
    const raw = localStorage.getItem("IBIS-language")
    const savedLanguage = raw ? raw.trim().toLowerCase() : null
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const raw2 = localStorage.getItem("IBIS-language")
        const lang = raw2 ? raw2.trim().toLowerCase() : null
        if (lang && translations[lang]) {
          setCurrentLanguage(lang)
          document.documentElement.dir = lang === "ar" ? "rtl" : "ltr"
          document.documentElement.lang = lang
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  // Language change handler
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)

    // Update document direction for Arabic
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  useEffect(() => {
    if (!selectedRestaurant) return
    fetchMenus(selectedRestaurant._id)

  }, [selectedRestaurant])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize() // Check initial size
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Auto-rotate header images in modern view
  useEffect(() => {
    if (viewMode !== "modern" || !selectedRestaurant || menus.length === 0) return
    const currentMenu = menus[currentMenuIndex]
    if (!currentMenu?.images || currentMenu.images.length <= 1) return
    const interval = setInterval(() => {
      setHeaderImageIndex((prev) => (prev + 1) % currentMenu.images.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [viewMode, selectedRestaurant, menus, currentMenuIndex])

  const nextMenu = () => {
    if (currentMenuIndex < menus.length - 1) {
      const menuContainer = document.querySelector(".menu-spread-new")
      if (menuContainer) {
        menuContainer.classList.add("slide-out-left-new")
        setTimeout(() => {
          setCurrentMenuIndex(currentMenuIndex + 1)
          setCurrentImageIndex(0) // Reset image index when changing menu
          menuContainer.classList.remove("slide-out-left-new")
          menuContainer.classList.add("slide-in-right-new")
          setTimeout(() => {
            menuContainer.classList.remove("slide-in-right-new")
          }, 300)
        }, 300)
      } else {
        setCurrentMenuIndex(currentMenuIndex + 1)
        setCurrentImageIndex(0)
      }
    }
  }

  const prevMenu = () => {
    if (currentMenuIndex > 0) {
      const menuContainer = document.querySelector(".menu-spread-new")
      if (menuContainer) {
        menuContainer.classList.add("slide-out-right-new")
        setTimeout(() => {
          setCurrentMenuIndex(currentMenuIndex - 1)
          setCurrentImageIndex(0) // Reset image index when changing menu
          menuContainer.classList.remove("slide-out-right-new")
          menuContainer.classList.add("slide-in-left-new")
          setTimeout(() => {
            menuContainer.classList.remove("slide-in-left-new")
          }, 300)
        }, 300)
      } else {
        setCurrentMenuIndex(currentMenuIndex - 1)
        setCurrentImageIndex(0)
      }
    }
  }

  // Image navigation functions
  const nextImage = () => {
    const currentMenu = menus[currentMenuIndex]
    if (currentMenu && currentMenu.images && currentImageIndex < currentMenu.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
  }

  // Change to a specific menu by index
  const goToMenu = (index) => {
    if (index !== currentMenuIndex) {
      setCurrentMenuIndex(index)
      setCurrentImageIndex(0)
      setHeaderImageIndex(0)
    }
  }

  // Get filtered items for the current menu
  const getCurrentMenuItems = () => {
    if (!menus.length) return []
    const currentMenu = menus[currentMenuIndex]
    let activeItems = filterItemsForVenueClient(currentMenu)

    if (showVegetarianOnly) {
      activeItems = activeItems.filter((item) => item.isVegetarian === true)
    }

    return activeItems
  }

  // Toggle between view modes
  const toggleViewMode = () => {
    setViewMode(viewMode === "book" ? "modern" : "book")
    setHeaderImageIndex(0)
  }

  // Restaurant card variants for animation
  const restaurantVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      },
    }),
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  return (
    <div className={`hotel-app-new ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>
        {`
        /* Language dropdown styles */
        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }
        
        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }
        
        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }
        
        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }
        
        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        /* RTL Support */
        .rtl {
          direction: rtl;
        }

        .rtl .language-selector {
          left: auto;
          right: 15px;
        }

        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }

        .rtl .view-toggle-button {
          right: 15px;
          left: auto;
        }

        .rtl .header-back-link-new {
          flex-direction: row;
        }

        .rtl .header-back-link-new svg {
          margin-right: 8px;
          margin-left: 0;
        }

        .rtl .welcome-banner-new h1 {
          text-align: right;
        }

        .rtl .welcome-banner-new p {
          text-align: right;
        }

        .rtl .loading-container-new p,
        .rtl .empty-state-new h3,
        .rtl .empty-state-new p {
          text-align: right;
        }

        .rtl .restaurant-card-content-new {
          text-align: right;
        }

        .rtl .restaurant-card-buttons-new {
          flex-direction: row-reverse;
        }

        .rtl .view-menu-button-new {
          margin-left: 10px;
          margin-right: 0;
        }

        .rtl .modern-header-title {
          text-align: right;
        }

        .rtl .modern-menu-tabs {
          direction: rtl; /* Ensure tabs scroll correctly */
        }

        .rtl .menu-tab {
          border-left: 1px solid #e9ecef;
          border-right: none;
        }

        .rtl .modern-allergen svg {
          margin-right: 0;
          margin-left: 5px;
        }

        .rtl .modern-item-image {
          margin-right: 0;
          margin-left: 15px;
        }

        .rtl .modern-item-header {
          flex-direction: row-reverse;
        }

        .rtl .modern-item-content {
          text-align: right;
        }

        .rtl .menu-items-side-new {
          text-align: right;
        }

        .rtl .menu-navigation-new .nav-button-new.prev svg {
          transform: scaleX(-1);
        }

        .rtl .menu-navigation-new .nav-button-new.next svg {
          transform: scaleX(-1);
        }

        .rtl .image-nav-btn-new.prev-image-new svg {
          transform: scaleX(-1);
        }

        .rtl .image-nav-btn-new.next-image-new svg {
          transform: scaleX(-1);
        }

        .rtl .image-counter-new {
          direction: ltr; /* Keep numbers LTR even in RTL context */
        }

        .rtl .image-dots-new {
          direction: ltr; /* Keep dots LTR even in RTL context */
        }

        .rtl .modal h2,
        .rtl .modal input,
        .rtl .modal-actions-new {
          text-align: right;
        }

        .rtl .modal-actions-new button {
          margin-left: 10px;
          margin-right: 0;
        }

        .rtl .footer-section {
          text-align: right;
        }

        .rtl .copyright {
          text-align: right;
        }

        .rtl .copyright a {
          margin-left: 0;
          margin-right: 5px;
        }

        /* Fixed image dimensions CSS - Smaller size to fit mobile containers */
        .menu-image-carousel-fixed {
          position: relative !important;
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 12px !important;
          padding: 12px !important;
        }
        .carousel-container-fixed {
          position: relative !important;
          width: 180px !important;
          height: 140px !important;
          margin: 0 auto 12px auto !important;
          overflow: hidden !important;
          border-radius: 8px !important;
          flex-shrink: 0 !important;
          display: block !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
        }
        .menu-image-fixed {
          width: 180px !important;
          height: 140px !important;
          min-width: 180px !important;
          min-height: 140px !important;
          max-width: 180px !important;
          max-height: 140px !important;
          object-fit: cover !important;
          object-position: center !important;
          display: block !important;
          border-radius: 8px !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .menu-image-side-fixed {
          flex: 1 !important;
          position: relative !important;
          background: #f8f9fa !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 12px !important;
          width: 100% !important;
          overflow: visible !important;
        }
        /* Mobile specific overrides */
        @media (max-width: 768px) {
          .menu-image-carousel-fixed {
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 12px !important;
            padding: 12px !important;
            width: 100% !important;
            min-height: 100% !important;
          }
          .carousel-container-fixed {
            width: 180px !important;
            height: 140px !important;
            margin: 0 auto 12px auto !important;
            flex-shrink: 0 !important;
            overflow: hidden !important;
            display: block !important;
          }
          .menu-image-fixed {
            width: 180px !important;
            height: 140px !important;
            min-width: 180px !important;
            min-height: 140px !important;
            max-width: 180px !important;
            max-height: 140px !important;
            object-fit: cover !important;
            object-position: center !important;
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .menu-image-side-fixed {
            width: 100% !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            overflow: visible !important;
          }
        }
        /* Very small screens - even smaller images */
        @media (max-width: 480px) {
          .carousel-container-fixed {
            width: 160px !important;
            height: 120px !important;
            margin: 0 auto 10px auto !important;
          }
          .menu-image-fixed {
            width: 160px !important;
            height: 120px !important;
            min-width: 160px !important;
            min-height: 120px !important;
            max-width: 160px !important;
            max-height: 120px !important;
          }
        }
        /* Extra small screens */
        @media (max-width: 360px) {
          .carousel-container-fixed {
            width: 140px !important;
            height: 100px !important;
            margin: 0 auto 8px auto !important;
          }
          .menu-image-fixed {
            width: 140px !important;
            height: 100px !important;
            min-width: 140px !important;
            min-height: 100px !important;
            max-width: 140px !important;
            max-height: 100px !important;
          }
        }
        /* Modern view styles */
        .view-toggle-button {
          position: absolute;
          top: 10px;
          left: 3px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          z-index: 10;
          transition: all 0.3s ease;
        }
        .view-toggle-button:hover {
          transform: scale(1.1);
          background: var(--primary-dark);
        }

        .app-main-new {
          min-height: 100vh;
          padding: 100px 20px 40px;
          background: linear-gradient(180deg, #f5f7fb 0%, #ffffff 100%);
        }

        .welcome-banner-new {
          max-width: 980px;
          margin: 0 auto 32px;
          padding: 32px 28px;
          border-radius: 24px;
          background: linear-gradient(135deg, rgba(0, 71, 171, 0.08), rgba(56, 189, 248, 0.08));
          box-shadow: 0 24px 68px rgba(15, 23, 42, 0.08);
          text-align: center;
        }

        .welcome-banner-new h1 {
          margin: 0 0 14px;
          font-size: clamp(1.9rem, 2.2vw, 2.8rem);
          line-height: 1.05;
          color: #0f172a;
        }

        .welcome-banner-new p {
          margin: 0;
          font-size: 1rem;
          line-height: 1.8;
          color: #475569;
        }

        .restaurants-grid-new {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
          width: 100%;
          max-width: 1220px;
          margin: 0 auto 32px;
        }

        .restaurant-card-new {
          display: flex;
          flex-direction: column;
          min-height: 100%;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }

        .restaurant-card-new:hover {
          transform: translateY(-4px);
          box-shadow: 0 30px 72px rgba(15, 23, 42, 0.12);
          border-color: rgba(0, 71, 171, 0.18);
        }

        .restaurant-card-image-new {
          position: relative;
          width: 100%;
          min-height: 220px;
          overflow: hidden;
          background: #e2e8f0;
        }

        .restaurant-card-image-new img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .restaurant-card-content-new {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 16px;
          padding: 22px;
          flex: 1;
        }

        .restaurant-card-title-new {
          margin: 0;
          font-size: 1.15rem;
          line-height: 1.3;
          font-weight: 700;
          color: #0f172a;
        }

        .restaurant-card-price-new {
          margin: 0;
          color: #0f766e;
          font-weight: 700;
          font-size: 1rem;
        }

        .restaurant-card-buttons-new {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: flex-start;
          margin-top: auto;
        }

        .view-menu-button-new,
        .reserve-button-new {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          padding: 0 18px;
          border-radius: 999px;
          border: none;
          font-weight: 600;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .view-menu-button-new {
          background: var(--primary);
          color: white;
        }

        .view-menu-button-new:hover {
          background: #003a8c;
        }

        .reserve-button-new {
          background: #0f766e;
          color: white;
        }

        .reserve-button-new:hover {
          background: #145a52;
        }

        .loading-container-new,
        .empty-state-new {
          max-width: 760px;
          margin: 0 auto 32px;
          padding: 28px 24px;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 22px 54px rgba(15, 23, 42, 0.08);
          text-align: center;
        }

        .empty-state-new h3 {
          margin: 0 0 12px;
          font-size: 1.5rem;
          color: #0f172a;
        }

        .empty-state-new p,
        .loading-container-new p {
          margin: 0;
          color: #475569;
          font-size: 1rem;
          line-height: 1.7;
        }

        .loading-spinner-new {
          width: 44px;
          height: 44px;
          border: 5px solid rgba(71, 85, 105, 0.18);
          border-top: 5px solid var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 18px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .menu-display-container-new {
          width: 100%;
          max-width: 1240px;
          margin: 0 auto;
          padding: 18px 16px 28px;
        }

        .menu-spread-new {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          justify-content: center;
          align-items: flex-start;
        }

        .menu-content-new {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          justify-content: center;
          width: 100%;
          padding: 20px 0 0;
        }

        .menu-image-side-new {
          flex: 0 0 min(420px, 100%);
          min-width: 280px;
          border-radius: 24px;
          overflow: hidden;
          background: white;
          box-shadow: 0 22px 45px rgba(15, 23, 42, 0.07);
        }

        .menu-items-side-new {
          flex: 1 1 min(380px, 100%);
          background: white;
          border-radius: 24px;
          box-shadow: 0 22px 45px rgba(15, 23, 42, 0.07);
          padding: 24px;
        }

        .menu-item-new {
          background: #f8fafc;
          border-radius: 18px;
          padding: 18px 20px;
          border: 1px solid rgba(15, 23, 42, 0.06);
          margin-bottom: 16px;
        }

        .menu-item-header-new {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .menu-item-name-new {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #0f172a;
        }

        .menu-item-price-new {
          color: var(--primary);
          font-weight: 700;
          font-size: 1rem;
          white-space: nowrap;
        }

        .menu-item-description-new {
          margin: 0;
          color: #475569;
          font-size: 0.975rem;
          line-height: 1.75;
        }

        .menu-item-allergens-new {
          margin-top: 0.75rem;
        }

        .content-description {
          width: 100%;
          max-width: 860px;
          margin: 0 auto;
          padding: 0 6px;
          text-align: left;
        }

        .page-content-restaurant {
          margin: 20px auto 28px;
          max-width: 980px;
          padding: 24px;
          text-align: center;
          background: transparent;
          border-radius: 0;
          border: none;
          box-shadow: none;
        }

        .page-content-restaurant img.page-content-image {
          display: block;
          margin: 0 auto 24px;
          max-width: 100%;
          height: auto;
          border-radius: 0;
          box-shadow: none;
        }

        .page-content-restaurant .page-content-html-light {
          font-size: 1rem;
          color: #334155;
          line-height: 1.8;
          margin: 0;
        }

        .page-content-restaurant .page-content-html-light p {
          margin-bottom: 1.2rem;
        }

        .page-content-restaurant .page-content-html-light ul,
        .page-content-restaurant .page-content-html-light ol {
          margin: 0 0 1rem 1.4rem;
          padding-left: 1rem;
        }

        .page-content-video-wrap {
          width: 100%;
          margin-bottom: 20px;
          border-radius: 18px;
          overflow: hidden;
          background: #000;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
        }

        .page-content-video {
          width: 100%;
          max-height: min(70vh, 520px);
          display: block;
          vertical-align: middle;
        }

        @media (max-width: 768px) {
          .app-main-new {
            padding: 80px 16px 32px;
          }

          .welcome-banner-new {
            margin-bottom: 24px;
            padding: 24px 20px;
          }

          .restaurants-grid-new {
            gap: 18px;
          }

          .menu-display-container-new {
            padding: 16px 0 24px;
          }

          .menu-content-new {
            gap: 18px;
          }

          .menu-image-side-new,
          .menu-items-side-new {
            border-radius: 20px;
            padding: 18px;
          }
        }

        .modern-menu-container {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          padding: 0;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .modern-header-image {
          width: 100%;
          height: 200px;
          position: relative;
          overflow: hidden;
        }
        .modern-header-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: opacity 0.5s ease;
        }
        .modern-header-title {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          padding: 20px;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
          color: white;
        }
        .modern-header-title h2 {
          margin: 0;
          font-size: 28px;
          font-weight: 600;
        }
        .modern-menu-tabs {
          display: flex;
          overflow-x: auto;
          padding: 0;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          scrollbar-width: none;
        }
        .modern-menu-tabs::-webkit-scrollbar {
          display: none;
        }
        .menu-tab {
          padding: 15px 20px;
          background: white;
          border: none;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
          color: #495057;
          border-right: 1px solid #e9ecef;
        }
        .menu-tab.active {
          background: var(--primary);
          color: white;
        }
        .modern-allergens {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }
        .modern-allergen {
          display: flex;
          align-items: center;
          font-size: 13px;
          color: #495057;
          background: white;
          padding: 5px 10px;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .modern-allergen svg {
          margin-right: 5px;
        }
        .modern-menu-items {
          padding: 15px;
          max-height: 60vh;
          overflow-y: auto;
        }
        .modern-section {
          margin-bottom: 30px;
        }
        .modern-section-title {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          padding-bottom: 5px;
          border-bottom: 2px solid var(--primary);
        }
        .modern-item {
          display: flex;
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e9ecef;
        }
        .modern-item-image {
          width: 80px;
          height: 80px;
          border-radius: 8px;
          overflow: hidden;
          margin-right: 15px;
          flex-shrink: 0;
        }
        .modern-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .modern-item-content {
          flex: 1;
        }
        .modern-item-header {
  display: flex;
  justify-content: space-between; /* name left, price right */
  align-items: flex-start;        /* top align when name wraps */
  width: 100%;
}

.modern-item-title {
  flex: 1;                        /* take remaining space */
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  margin-right: 16px;             /* ← fixed spacing before price */

  /* allow wrapping (remove ellipsis) */
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.modern-item-price {
  flex-shrink: 0;                 /* never shrink */
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;            /* keep "45 TND" in one line */
  font-weight: 600;
  color: var(--primary);
  font-size: 18px;
}

        .modern-item-description {
          color: #6c757d;
          font-size: 14px;
          margin: 0;
        }
        .modern-item-weight {
          font-size: 12px;
          color: #adb5bd;
          margin-top: 5px;
        }
        .modern-grid-items {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        .modern-grid-item {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .modern-grid-image {
          width: 100%;
          height: 150px;
        }
        .modern-grid-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .modern-grid-content {
          padding: 15px;
        }
        .modern-grid-title {
          font-size: 16px;
          font-weight: 500;
          margin: 0 0 5px 0;
        }
        .modern-grid-price {
          font-weight: 600;
          color: var(--primary);
        }
        .modern-grid-description {
          font-size: 14px;
          color: #6c757d;
          margin: 10px 0;
        }
        .modern-grid-weight {
          font-size: 12px;
          color: #adb5bd;
          margin-top: 5px;
        }
        .add-to-cart-button {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 16px;
          font-size: 14px;
          cursor: pointer;
          margin-top: 10px;
          transition: all 0.2s ease;
        }
        .add-to-cart-button:hover {
          background: var(--primary-dark);
        }
        @media (max-width: 768px) {
          .modern-item {
            flex-direction: column;
          }
          .modern-item-image {
            width: 100%;
            height: 150px;
            margin-right: 0;
            margin-bottom: 10px;
          }
          .modern-grid-items {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }
        }

/* Removed conflicting vegetarian filter styles */
        .vegetarian-filter-header {
          display: flex;
          justify-content: center;
          padding: 15px 20px;
          background: rgba(0, 0, 0, 0.05);
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }

        .vegetarian-toggle {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--primary);
          color: white;
          padding: 10px 20px;
          border-radius: 25px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          font-size: 14px;
          font-weight: 500;
        }

        .vegetarian-toggle:hover {
          background: var(--primary-dark);
          transform: translateY(-1px);
        }

        .vegetarian-toggle input[type="checkbox"] {
          position: relative;
          width: 40px;
          height: 20px;
          appearance: none;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          outline: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .vegetarian-toggle input[type="checkbox"]:checked {
          background: rgba(255, 255, 255, 0.8);
        }

        .vegetarian-toggle input[type="checkbox"]:before {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .vegetarian-toggle input[type="checkbox"]:checked:before {
          transform: translateX(20px);
          background: var(--primary);
        }

        .vegetarian-toggle-label {
          color: white;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        /* Added styles for dietary legend */
        .dietary-legend {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .dietary-legend h4 {
          margin: 0 0 10px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .legend-items {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        .legend-item {
          font-size: 14px;
          color: #666;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .legend-items-allergens {
          gap: 8px;
        }

        .legend-item-allergen {
          font-size: 12px;
          color: #b91c1c;
          background: #fff5f5;
          border: 1px solid #fecaca;
          border-radius: 999px;
          padding: 4px 10px;
          gap: 5px;
        }

        .allergen-legend-icon,
        .allergen-badge-icon {
          display: inline-flex;
          flex-shrink: 0;
        }

        .allergen-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 600;
          color: #b91c1c;
          background: #fff5f5;
          border: 1px solid #fecaca;
          border-radius: 999px;
          padding: 2px 8px;
          margin: 2px 4px 2px 0;
        }

        .menu-item-allergens-new,
        .modern-item-allergens {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 0.5rem;
        }

        /* Updated dietary badge styles to be more generic */
        .dietary-badge {
          margin-left: 6px;
          font-size: 16px;
        }

        .vegetarian-badge {
          margin-left: 6px;
          font-size: 16px;
        }

        /* Mobile responsive for legend */
        @media (max-width: 768px) {
          .dietary-legend {
            padding: 12px;
          }
          
          .legend-items {
            gap: 10px;
          }
          
          .legend-item {
            font-size: 13px;
          }
          
          .dietary-badge {
            font-size: 14px;
            margin-left: 4px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .vegetarian-filter-header {
            padding: 10px 15px;
          }
          
          .vegetarian-toggle {
            padding: 8px 15px;
            font-size: 13px;
          }
        }

        .reservation-feedback-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 20, 50, 0.55);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 1.25rem;
        }

        .reservation-feedback-modal {
          background: #fff;
          border-radius: 20px;
          padding: 2rem 1.75rem 1.5rem;
          width: 100%;
          max-width: 380px;
          text-align: center;
          box-shadow: 0 24px 60px rgba(0, 71, 171, 0.25);
          border: 1px solid rgba(231, 44, 61, 0.12);
        }

        .reservation-feedback-modal.success {
          border-top: 4px solid #10b981;
        }

        .reservation-feedback-modal.error {
          border-top: 4px solid #e72c3d;
        }

        .feedback-icon-circle {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.25rem;
        }

        .feedback-icon-circle.success {
          background: rgba(16, 185, 129, 0.12);
          color: #10b981;
        }

        .feedback-icon-circle.error {
          background: rgba(231, 44, 61, 0.12);
          color: #e72c3d;
        }

        .reservation-feedback-modal h3 {
          color: #1a1a2e;
          font-size: 1.35rem;
          font-weight: 700;
          margin: 0 0 0.75rem;
          line-height: 1.3;
        }

        .reservation-feedback-modal p {
          color: #64748b;
          font-size: 0.95rem;
          line-height: 1.55;
          margin: 0 0 0.5rem;
        }

        .feedback-detail {
          display: inline-block;
          background: rgba(0, 71, 171, 0.08);
          color: #0047ab;
          font-weight: 600;
          font-size: 0.88rem;
          padding: 0.35rem 0.85rem;
          border-radius: 999px;
          margin-bottom: 0.5rem !important;
        }

        .feedback-ok-btn {
          width: 100%;
          margin-top: 1.25rem;
          padding: 0.85rem 1rem;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #e72c3d 0%, #c41e3a 100%);
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 4px 14px rgba(231, 44, 61, 0.35);
        }

        .feedback-ok-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(231, 44, 61, 0.45);
        }

        .feedback-ok-btn:active {
          transform: translateY(0);
        }

        .modal-actions-new button:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .rtl .reservation-feedback-modal {
          text-align: center;
        }
      `}
      </style>
      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header-new">
        <button
          className="header-back-link-new"
          onClick={() => {
            if (selectedRestaurant) {
              setSelectedRestaurant(null)
              setMenus([])
              setCurrentMenuIndex(0)
              setCurrentImageIndex(0)
            } else {
              window.location.href = "/RestaurantBarClient"
            }
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("back")}
        </button>
        <div className="logo-container-new">
          <img src="/images/logo2.webp" alt="Ibis Logo" className="logo-new" />
        </div>
        <div></div>
      </header>
      <main className="app-main-new">
{pageContent && (
  <div className="page-content-restaurant">
    {pageContent.video && (
      <div className="page-content-video-wrap">
        <video
          src={pageContent.video}
          controls
          playsInline
          preload="metadata"
          className="page-content-video"
        />
      </div>
    )}
    {getPageContentHeroImageUrl() && (
      <img
        src={getPageContentHeroImageUrl()}
        alt="Restaurant"
        className="page-content-image"
        onError={(e) => (e.target.src = "/placeholder.svg")}
      />
    )}
 {getPageContentDescription() && (
            <div className="content-description">
              <div
  className="page-content-html-light"
  dangerouslySetInnerHTML={{ __html: getPageContentDescription() }}
/>

            </div>
          )}
  </div>
)}
        {!selectedRestaurant ? (
          <>
            
            
            {isLoading ? (
              <div className="loading-container-new">
                <div className="loading-spinner-new"></div>
                <p>{t("loadingRestaurants")}</p>
              </div>
            ) : restaurants.length === 0 ? (
              <div className="empty-state-new">
                <div className="empty-icon-new">🍽️</div>
                <h3>{t("noRestaurantsFound")}</h3>
                <p>{t("comeBackSoonRestaurants")}</p>
              </div>
            ) : (
              <div className="restaurants-grid-new">
                {restaurants.map((restaurant, index) => {
                  const venuePrice = getVenuePriceAmount(restaurant)
                  return (
                  <motion.div
                    key={restaurant._id}
                    className="restaurant-card-new"
                    custom={index}
                    variants={restaurantVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{
                      y: -10,
                      boxShadow: "0 15px 30px rgba(0, 71, 171, 0.2)",
                      borderColor: "var(--primary)",
                    }}
                  >
                    <div className="restaurant-card-image-new">
                      <img
                        src={restaurant.image || "/placeholder.svg"}
                        alt={getRestaurantName(restaurant)}
                        onError={(e) => (e.target.src = "/placeholder.svg")}
                      />
                    </div>
                    <div className="restaurant-card-content-new">
                      <h3 className="restaurant-card-title-new">{getRestaurantName(restaurant)}</h3>
                      {venuePrice ? (
                        <p className="restaurant-card-price-new">{venuePrice} TND</p>
                      ) : null}
                      <div className="restaurant-card-buttons-new">
                        <button
                          className="view-menu-button-new"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedRestaurant(restaurant)
                          }}
                        >
                          {t("viewMenu")}
                        </button>
                        {restaurant.reservable && (
                          <button
                            className="reserve-button-new"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRestaurant(restaurant)
                              setShowModal(true)
                            }}
                          >
                            {t("reserveTable")}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  )
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {isLoading ? (
              <div className="loading-container-new">
                <div className="loading-spinner-new"></div>
                <p>{t("loadingMenus")}</p>
              </div>
            ) : menus.length === 0 ? (
              <div className="empty-state-new">
                <div className="empty-icon-new">🍽️</div>
                <h3>{t("noMenuAvailable")}</h3>
                <p>{t("comeBackSoonMenus")}</p>
              </div>
            ) : (
              <div className="menu-display-container-new" style={{ position: "relative" }}>
                <button
                  className="view-toggle-button"
                  onClick={toggleViewMode}
                  aria-label={viewMode === "book" ? t("switchToModern") : t("switchToBook")}
                >
                  {viewMode === "book" ? <LayoutGrid size={20} /> : <Book size={20} />}
                </button>

                {/* Removed DietaryLegend from here - it will only show in modern view now */}

                {viewMode === "book" ? (
                  // Original Book View
                  <>
                    {menus.length > 1 && (
                      <div
                        className="book-menu-tabs"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          justifyContent: "center",
                          marginBottom: "12px",
                          padding: "0 8px",
                        }}
                      >
                        {menus.map((menu, index) => (
                          <button
                            key={menu._id || index}
                            type="button"
                            onClick={() => goToMenu(index)}
                            style={{
                              padding: "8px 14px",
                              borderRadius: "8px",
                              border:
                                currentMenuIndex === index
                                  ? "2px solid var(--primary, #0047ab)"
                                  : "1px solid #ccc",
                              background: currentMenuIndex === index ? "rgba(0, 71, 171, 0.08)" : "#fff",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {getMenuTitle(menu)}
                          </button>
                        ))}
                      </div>
                    )}
                    {currentMenuIndex >= 0 && currentMenuIndex < menus.length && (
                      <div className="menu-spread-new" ref={bookRef}>
                        <div className="menu-content-new">
                          {/* Left side - Menu Images with Carousel */}
                          <div className={`menu-image-side-new ${isMobile ? "menu-image-side-fixed" : ""}`}>
                            {menus[currentMenuIndex].images && menus[currentMenuIndex].images.length > 0 ? (
                              <div
                                className={`menu-image-carousel-new menu-image-carousel-new-dark ${
                                  isMobile ? "menu-image-carousel-fixed" : ""
                                }`}
                              >
                                {isMobile ? (
                                  // Small screens: Show all images vertically with fixed dimensions
                                  menus[currentMenuIndex].images.map((image, index) => (
                                    <div key={index} className="carousel-container-fixed">
                                      <img
                                        src={image || "/placeholder.svg"}
                                        alt={`${getMenuTitle(menus[currentMenuIndex])} - Image ${index + 1}`}
                                        className="menu-image-fixed"
                                        onError={(e) => (e.target.src = "/placeholder.svg")}
                                      />
                                    </div>
                                  ))
                                ) : (
                                  // Large screens: Show carousel with navigation
                                  <>
                                    <div className="carousel-container-new carousel-container-new-dark">
                                      <img
                                        src={menus[currentMenuIndex].images[currentImageIndex] || "/placeholder.svg"}
                                        alt={`${getMenuTitle(menus[currentMenuIndex])} - Image ${currentImageIndex + 1}`}
                                        className="menu-image-new menu-image-new-dark"
                                        onError={(e) => (e.target.src = "/placeholder.svg")}
                                      />
                                      {/* Navigation arrows - only on large screens */}
                                      {menus[currentMenuIndex].images.length > 1 && (
                                        <>
                                          <button
                                            className="image-nav-btn-new image-nav-btn-new-dark prev-image-new prev-image-new-dark"
                                            onClick={prevImage}
                                            disabled={currentImageIndex === 0}
                                            aria-label={t("previousImage")}
                                          >
                                            <ChevronLeft className="h-4 w-4" />
                                          </button>
                                          <button
                                            className="image-nav-btn-new image-nav-btn-new-dark next-image-new next-image-new-dark"
                                            onClick={nextImage}
                                            disabled={currentImageIndex === menus[currentMenuIndex].images.length - 1}
                                            aria-label={t("nextImage")}
                                          >
                                            <ChevronRight className="h-4 w-4" />
                                          </button>
                                        </>
                                      )}
                                      {/* Image counter - only on large screens */}
                                      {menus[currentMenuIndex].images.length > 1 && (
                                        <div className="image-counter-new image-counter-new-dark">
                                          {currentImageIndex + 1} / {menus[currentMenuIndex].images.length}
                                        </div>
                                      )}
                                    </div>
                                    {/* Image dots indicator - only on large screens */}
                                    {menus[currentMenuIndex].images.length > 1 && (
                                      <div className="image-dots-new image-dots-new-dark">
                                        {menus[currentMenuIndex].images.map((_, index) => (
                                          <button
                                            key={index}
                                            className={`image-dot-new image-dot-new-dark ${
                                              index === currentImageIndex ? "active" : ""
                                            }`}
                                            onClick={() => goToImage(index)}
                                            aria-label={`${t("goToImage")} ${index + 1}`}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className="menu-image-placeholder-new">
                                <span>{getMenuTitle(menus[currentMenuIndex])}</span>
                              </div>
                            )}
                          </div>
                          {/* Right side - Menu Items */}
                          <div className="menu-items-side-new">
                            <h2 className="menu-title-new">{getMenuTitle(menus[currentMenuIndex])}</h2>
                            {getCurrentMenuItems().length > 0 ? (
                              <div className="menu-items-list-new">
                                {getCurrentMenuItems().map((item, idx) => {
                                  const price = getItemPriceAmount(item)
                                  return (
                                  <div key={idx} className="menu-item-new">
                                    <div className="menu-item-header-new">
                                      <h3 className="menu-item-name-new">
                                        {getItemName(item)}
                                        {item.isVegetarian && <span className="vegetarian-badge">🌱</span>}
                                      </h3>
                                      <div className="menu-item-price-new">
                                        {price ? `${price} TND` : ""}
                                      </div>
                                    </div>
                                    <p className="menu-item-description-new">{getItemDescription(item)}</p>
                                    {renderAllergenBadges(item, currentLanguage).length > 0 && (
                                      <div className="menu-item-allergens-new">
                                        {renderAllergenBadges(item, currentLanguage)}
                                      </div>
                                    )}
                                  </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="empty-menu-new">
                                <p>
                                  {showVegetarianOnly
                                    ? t("noVegetarianDishes") || "Aucun plat végétarien disponible"
                                    : t("noDishAvailable")}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="menu-navigation-new">
                      <button
                        className="nav-button-new prev"
                        onClick={prevMenu}
                        disabled={currentMenuIndex === 0}
                        aria-label={t("previousMenu")}
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        className="nav-button-new next"
                        onClick={nextMenu}
                        disabled={currentMenuIndex === menus.length - 1}
                        aria-label={t("nextMenu")}
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </div>
                  </>
                ) : (
                  // Modern View
                  <div className="modern-menu-container">
                    {/* Header Image */}
                    <div className="modern-header-image">
                      {menus[currentMenuIndex].images && menus[currentMenuIndex].images.length > 0 ? (
                        <img
                          src={menus[currentMenuIndex].images[headerImageIndex] || "/placeholder.svg"}
                          alt={getMenuTitle(menus[currentMenuIndex])}
                          onError={(e) => (e.target.src = "/placeholder.svg")}
                        />
                      ) : (
                        <img src="/placeholder.svg" alt={t("noMenuAvailable")} />
                      )}
                      <div className="modern-header-title">
                        <h2>{getMenuTitle(menus[currentMenuIndex])}</h2>
                      </div>
                    </div>
                    {/* Menu Tabs - Showing all menus */}
                    <div className="modern-menu-tabs">
                      {menus.map((menu, index) => (
                        <button
                          key={index}
                          className={`menu-tab ${currentMenuIndex === index ? "active" : ""}`}
                          onClick={() => goToMenu(index)}
                        >
                          {getMenuTitle(menu)}
                        </button>
                      ))}
                    </div>

                    <DietaryLegend />

                    {/* Menu Items */}
                    <div className="modern-menu-items">
                      {getCurrentMenuItems().length > 0 ? (
                        <>
                          {/* Group items by category if they have categories */}
                          {Object.entries(
                            getCurrentMenuItems().reduce((acc, item) => {
                              const category = item.category || "Autres" // Default category if not specified
                              if (!acc[category]) acc[category] = []
                              acc[category].push(item)
                              return acc
                            }, {}),
                          ).map(([category, items]) => (
                            <div key={category} className="modern-section">
                              {/* <h3 className="modern-section-title">{category}</h3> */}
                              {items.map((item, idx) => {
                                const price = getItemPriceAmount(item)
                                return (
                                <div key={idx} className="modern-item">
                                  {item.image && (
                                    <div className="modern-item-image">
                                      <img
                                        src={item.image || "/placeholder.svg"}
                                        alt={getItemName(item)}
                                        onError={(e) => (e.target.src = "/placeholder.svg")}
                                      />
                                    </div>
                                  )}
                                  <div className="modern-item-content">
                                    <div className="modern-item-header">
                                      <div className="modern-item-title">
                                        {getItemName(item)}
                                        {renderDietaryBadges(item)}
                                      </div>
                                      <span className="modern-item-price">
                                        {price ? (
                                          <>
                                            {price}{" "}
                                            <span className="currency">TND</span>
                                          </>
                                        ) : null}
                                      </span>
                                    </div>
                                    <p className="modern-item-description">{getItemDescription(item)}</p>
                                    {renderAllergenBadges(item, currentLanguage).length > 0 && (
                                      <div className="modern-item-allergens">
                                        {renderAllergenBadges(item, currentLanguage)}
                                      </div>
                                    )}
                                    {item.weight && <div className="modern-item-weight">{item.weight} gr</div>}
                                  </div>
                                </div>
                                )
                              })}
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="empty-menu-new">
                          <p>
                            {showVegetarianOnly
                              ? t("noVegetarianDishes") || "Aucun plat végétarien disponible"
                              : t("noDishAvailable")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>

            <p>
                {t("availableAtReception")}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibisTunis" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/ibistunis/" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h2>{t("reserveTableModal")}</h2>
            <input
              type="text"
              placeholder={t("name")}
              value={reservationData.name}
              onChange={(e) => setReservationData({ ...reservationData, name: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder={t("email")}
              value={reservationData.email}
              onChange={(e) => setReservationData({ ...reservationData, email: e.target.value })}
              required
            />
            <input
              type="tel"
              placeholder={t("phoneNumber")}
              value={reservationData.phoneNumber}
              onChange={(e) => setReservationData({ ...reservationData, phoneNumber: e.target.value })}
              required
            />
            <input
              type="datetime-local"
              value={reservationData.from}
              onChange={(e) => setReservationData({ ...reservationData, from: e.target.value })}
              required
            />
            <input
              type="number"
              min="1"
              placeholder={t("numberOfPeople")}
              value={reservationData.people}
              onChange={(e) => setReservationData({ ...reservationData, people: e.target.value })}
              required
            />
            <div className="modal-actions-new">
              <button onClick={handleReservationSubmit} disabled={isSubmittingReservation}>
                {isSubmittingReservation ? t("submitting") : `✅ ${t("reserve")}`}
              </button>
              <button onClick={() => setShowModal(false)} disabled={isSubmittingReservation}>
                ❌ {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      {reservationFeedback && (
        <div
          className="reservation-feedback-backdrop"
          onClick={() => setReservationFeedback(null)}
        >
          <motion.div
            className={`reservation-feedback-modal ${reservationFeedback}`}
            initial={{ opacity: 0, scale: 0.88, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`feedback-icon-circle ${reservationFeedback}`}>
              {reservationFeedback === "success" ? (
                <CheckCircle2 size={40} strokeWidth={2.2} />
              ) : (
                <XCircle size={40} strokeWidth={2.2} />
              )}
            </div>
            <h3>
              {t(
                reservationFeedback === "success"
                  ? "reservationSuccessTitle"
                  : "reservationErrorTitle"
              )}
            </h3>
            <p>
              {t(
                reservationFeedback === "success"
                  ? "reservationSuccessMessage"
                  : "reservationError"
              )}
            </p>
            {reservationFeedback === "success" && selectedRestaurant && (
              <p className="feedback-detail">{getRestaurantName(selectedRestaurant)}</p>
            )}
            <button
              type="button"
              className="feedback-ok-btn"
              onClick={() => setReservationFeedback(null)}
            >
              {t("ok")}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default VenueMenusClient
